import { useState, useRef, useEffect } from 'react'
import toast from 'react-hot-toast'
import { Download, ChevronDown, FileText, Table, Printer } from 'lucide-react'
import { cn } from '../../utils/cn'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { usePermissions } from '../../hooks/usePermissions'

export type ExportColumn = { key: string; header: string; isCurrency?: boolean; isDate?: boolean }

interface ExportButtonProps {
  data: Record<string, unknown>[]
  columns: ExportColumn[]
  filename: string
  formats?: ('csv' | 'excel' | 'pdf')[]
  className?: string
  /**
   * When set, the button only renders if the current user has this permission.
   * Hidden (not disabled) when the user lacks it. Omit to always render
   * (backward-compatible default).
   */
  requiredPermission?: string
  /**
   * Optional async provider for the rows to export. When set, it is awaited on
   * export (and used instead of `data`), so a paginated list can export ALL
   * matching rows rather than just the current page. `data` is still used for the
   * empty-disabled check. Omit to export `data` as-is (backward-compatible).
   */
  fetchData?: () => Promise<Record<string, unknown>[]>
}

function toCsvValue(v: unknown): string {
  if (v === null || v === undefined) return ''
  const s = String(v)
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`
  return s
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function exportCSV(data: Record<string, unknown>[], columns: ExportColumn[], filename: string) {
  const BOM = '\uFEFF'
  const header = columns.map(c => toCsvValue(c.header)).join(',')
  const rows = data.map(row =>
    columns.map(c => toCsvValue(row[c.key])).join(',')
  )
  const csv = BOM + [header, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, `${filename}.csv`)
}

async function exportExcel(data: Record<string, unknown>[], columns: ExportColumn[], filename: string) {
  const XLSX = await import('xlsx')
  const wsData = [
    columns.map(c => c.header),
    ...data.map(row => columns.map(c => row[c.key] ?? '')),
  ]
  const ws = XLSX.utils.aoa_to_sheet(wsData)
  // Auto column widths
  ws['!cols'] = columns.map(c => ({ wch: Math.max(c.header.length, 12) }))
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Report')
  downloadXlsx(XLSX, wb, filename)
}

async function exportPDF(
  data: Record<string, unknown>[],
  columns: ExportColumn[],
  filename: string,
  title?: string
) {
  const { jsPDF } = await import('jspdf')
  const autoTable = (await import('jspdf-autotable')).default
  const doc = new jsPDF({ orientation: columns.length > 6 ? 'landscape' : 'portrait', unit: 'mm' })

  // Header
  doc.setFontSize(14)
  doc.setTextColor(40, 40, 40)
  doc.text(title ?? filename, 14, 15)
  doc.setFontSize(9)
  doc.setTextColor(120, 120, 120)
  doc.text(`Generated: ${new Date().toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' })}`, 14, 22)

  autoTable(doc, {
    head: [columns.map(c => c.header)],
    body: data.map(row => columns.map(c => String(row[c.key] ?? ''))),
    startY: 28,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [249, 115, 22], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    didDrawPage: () => {
      const pageCount = (doc as unknown as { internal: { getNumberOfPages: () => number } }).internal.getNumberOfPages()
      const pageNumber = (doc as unknown as { internal: { getCurrentPageInfo: () => { pageNumber: number } } }).internal.getCurrentPageInfo().pageNumber
      doc.setFontSize(7)
      doc.setTextColor(150)
      doc.text(
        `Page ${pageNumber} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - 14,
        doc.internal.pageSize.getHeight() - 6,
        { align: 'right' }
      )
    },
  })

  doc.save(`${filename}.pdf`)
}

export function ExportButton({ data, columns, filename, formats = ['csv', 'excel', 'pdf'], className, requiredPermission, fetchData }: ExportButtonProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { can } = usePermissions()

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  async function handleExport(fmt: 'csv' | 'excel' | 'pdf' | 'print') {
    setOpen(false)
    if (fmt === 'print') { window.print(); return }
    setLoading(true)
    try {
      // When a provider is given, pull ALL matching rows (across pages); otherwise
      // export the rows already in hand.
      const rows = fetchData ? await fetchData() : data
      if (fmt === 'csv') exportCSV(rows, columns, filename)
      if (fmt === 'excel') await exportExcel(rows, columns, filename)
      if (fmt === 'pdf') await exportPDF(rows, columns, filename, filename.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
    } catch (err) {
      toast.error('Export failed: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setLoading(false)
    }
  }

  const menuItems: { key: 'csv' | 'excel' | 'pdf' | 'print'; label: string; icon: React.ReactNode }[] = [
    ...(formats.includes('csv') ? [{ key: 'csv' as const, label: 'Download CSV', icon: <Table className="h-3.5 w-3.5" /> }] : []),
    ...(formats.includes('excel') ? [{ key: 'excel' as const, label: 'Download Excel', icon: <Table className="h-3.5 w-3.5 text-sage-600" /> }] : []),
    ...(formats.includes('pdf') ? [{ key: 'pdf' as const, label: 'Download PDF', icon: <FileText className="h-3.5 w-3.5 text-red-500" /> }] : []),
    { key: 'print' as const, label: 'Print', icon: <Printer className="h-3.5 w-3.5" /> },
  ]

  // Optional central gating: hide (don't disable) when the caller opts in with a
  // permission the user lacks. No prop = always render (backward-compatible).
  if (requiredPermission && !can(requiredPermission)) return null

  return (
    <div className={cn('relative', className)} ref={ref}>
      <button
        className="btn-secondary text-xs py-1.5 flex items-center gap-1.5"
        onClick={() => setOpen(o => !o)}
        disabled={loading || data.length === 0}
      >
        <Download className="h-3.5 w-3.5" />
        Export
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border border-gray-100 z-50 py-1">
          {menuItems.map(item => (
            <button
              key={item.key}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => handleExport(item.key)}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
